import { CloseIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  Flex,
  Input,
  Portal,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import { useChartTheme } from '@revelio/replots';
import { Button, IconButton } from '@revelio/ui';

import { SIDE_PANEL_WIDTH } from '../../constants/panelWidths';
import { TextContentEditor } from '../Chart/TextChart/TextContentEditor';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import styles from './ChartBuilderPanel.module.css';
import { ChartCustomizePanel } from './ChartCustomizePanel';
import {
  ChartTypeSelector,
  isUserSelectableChartType,
} from './ChartTypeSelector';
import { getChartBuilderPanelVisibility } from './chart-builder-panel-visibility';
import { getEffectiveChartSelectorConfig } from './chart-selector-config';
import { DataFilterSelector } from './DataFilterSelector';
import { DimensionSourceSelector } from './DimensionSourceSelector';
import { MetricSelector } from './MetricSelector';
import { MultiMetricSelector } from './MultiMetricSelector';
import { ReportParameterSelector } from './ReportParameterSelector';
import { SecondaryDimensionSelector } from './SecondaryDimensionSelector';
import { SnowflakeParameterList } from './SnowflakeParameterList';
import labelStyles from './section-label.module.css';
import { TableColumnsEditor } from './TableColumnsEditor';

function getExcludedBreakdownDimensions(
  dimensionSource: DimensionSource | undefined
): SecondaryDimensionValues[] {
  if (!dimensionSource || dimensionSource === 'primary') {
    return [];
  }
  if (dimensionSource === 'competitors') {
    return ['company', 'competitors'];
  }
  return [dimensionSource];
}

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.1875 4.25L3.89583 15.2292H13.1042L13.8125 4.25"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M6.73047 7.4375V11.6875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M10.2695 7.4375V11.6875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M2.125 3.89575H14.875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M6.05469 3.83703C6.25127 2.66435 7.2711 1.77075 8.49963 1.77075C9.72816 1.77075 10.748 2.66435 10.9446 3.83703"
      stroke="currentColor"
      strokeLinecap="square"
    />
  </svg>
);

type ChartConfigProps = {
  isOpen: boolean;
  item: PositionedItem | undefined;
  selectedIds?: Set<string>;
  clearSelection?: () => void;
  onClose: () => void;
};

export const ChartBuilderPanel = ({
  item,
  isOpen,
  selectedIds,
  clearSelection,
  onClose,
}: ChartConfigProps) => {
  const { dispatch, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const customReportsSnowflakeEnabled = useUnleashFlag(
    FeatureFlag.CustomReportsSnowflake
  );
  const { isNewTheme } = useChartTheme();

  const id = item?.id;
  const chartType = item?.plot?.chartType;
  const isSelectableChartType = chartType
    ? isUserSelectableChartType(chartType)
    : false;

  const metric = item?.plot?.defaultMetricOptions?.[0];
  const primaryFilterId = primaryFilter?.id;

  const selectorConfig = chartType
    ? getEffectiveChartSelectorConfig(chartType, {
        overrides: item?.plot?.selectorOverrides,
        metric,
        primaryFilterId,
      })
    : null;

  const isSnowflake = item?.plot?.dataSource === 'snowflake';

  const isBarChart = chartType === 'bar';
  const dimensionSource = item?.plot?.dimensionSource;
  const excludedBreakdownDimensions =
    getExcludedBreakdownDimensions(dimensionSource);
  const isStackedBarTimeseries =
    chartType === 'stacked-bar' && dimensionSource === 'timeseries';

  const panelVisibility = getChartBuilderPanelVisibility({
    chartType,
    hasPrimaryFilter: Boolean(primaryFilter),
    isReport,
    customReportsSnowflakeEnabled,
    dataSource: item?.plot?.dataSource,
  });

  const title = item?.title ?? '';
  const isMultiSelect = selectedIds != null && selectedIds.size > 1;

  const handleDeleteChart = () => {
    const idsToDelete = (() => {
      if (selectedIds != null && selectedIds.size > 0) {
        return Array.from(selectedIds);
      }
      if (id !== null && id !== undefined) {
        return [id];
      }
      return [];
    })();
    if (idsToDelete.length === 0) return;
    dispatch({ type: 'DELETE_ITEMS', payload: { ids: idsToDelete } });
    clearSelection?.();
    onClose();
  };

  const handleSubmitTitle = (value: string) => {
    if (id && value !== item?.title) {
      const newTitle = value.trim() === '' ? null : value;
      dispatch({ type: 'UPDATE_ITEM_TITLE', payload: { id, title: newTitle } });
    }
  };

  const [accordionIndex, setAccordionIndex] = useState(-1);

  const handleAccordionChange = (index: number) => {
    setAccordionIndex(index);
  };

  useEffect(() => {
    if (item && !chartType && accordionIndex !== 0) {
      setAccordionIndex(0);
    }
  }, [item, chartType, accordionIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="listbox"]')) return;
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [chartTabIndex, setChartTabIndex] = useState(0);

  const renderMetricSelector = () => {
    if (item === undefined) return null;
    if (chartType === 'table' && !isSnowflake) {
      return (
        <TableColumnsEditor item={item} isDisabled={!isSelectableChartType} />
      );
    }
    if (selectorConfig?.metricMode === 'multi' && !isSnowflake) {
      return (
        <MultiMetricSelector
          item={item}
          isDisabled={!isSelectableChartType}
          showUnitOfAnalysis={selectorConfig?.showUnitOfAnalysis ?? false}
        />
      );
    }
    if (selectorConfig?.metricMode === 'single') {
      return (
        <MetricSelector
          item={item}
          isDisabled={!isSelectableChartType}
          isReport={isReport}
        />
      );
    }
    return null;
  };

  const renderParameterSelector = () => {
    if (item === undefined) return null;
    if (isSnowflake && isReport && customReportsSnowflakeEnabled) {
      return (
        <SnowflakeParameterList
          item={item}
          isDisabled={!isSelectableChartType}
        />
      );
    }
    if (primaryFilter && selectorConfig?.showReportParameters && isReport) {
      return (
        <Box mt={3}>
          <ReportParameterSelector
            item={item}
            parameters={item.plot?.plotParameters ?? []}
            primaryFilter={primaryFilter}
          />
        </Box>
      );
    }
    return null;
  };

  const titleField = (
    <>
      <Text
        className={labelStyles.sectionLabel}
        marginBottom="0.5rem"
        fontSize="sm"
        fontWeight="600"
      >
        Title
      </Text>
      <Input
        placeholder="Untitled chart"
        value={title}
        onChange={(e) => handleSubmitTitle(e.target.value)}
        variant="outline"
        paddingX="0.75rem"
        h="29px"
        lineHeight="29px"
        fontSize="sm"
        isReadOnly={!isSelectableChartType}
      />
    </>
  );

  const deleteChartButton = (
    <Button
      size="sm"
      fullWidth
      className={styles.deleteChartBtn}
      color="destructive"
      variant="outlined"
      leftIcon={<TrashIcon />}
      onClick={handleDeleteChart}
    >
      Delete chart
    </Button>
  );

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        right={0}
        height="100vh"
        width={SIDE_PANEL_WIDTH}
        display="flex"
        flexDirection="column"
        className={styles.panel}
        boxShadow="lg"
        paddingTop={2}
        paddingBottom={8}
        px={2}
        transform={
          isOpen
            ? 'translate3d(0, 0, 0)'
            : `translate3d(${SIDE_PANEL_WIDTH}, 0, 0)`
        }
        transition="transform 0.3s ease-in-out"
        zIndex={100}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mr={5}
          flexShrink={0}
        >
          <Text
            className={styles.panelTitle}
            display="flex"
            alignItems="center"
            px={4}
            py={3}
          >
            {isMultiSelect
              ? `${selectedIds?.size ?? 0} charts selected`
              : 'Edit chart'}
          </Text>
          <Flex alignItems="center" gap={2}>
            <span className={styles.escBadge}>Esc</span>
            <IconButton
              aria-label="Close chart builder"
              icon={<CloseIcon boxSize={2.5} className={styles.closeIcon} />}
              size="sm"
              shape="squared"
              variant="ghost"
              onClick={onClose}
            />
          </Flex>
        </Flex>

        <Box className={styles.headerDivider} />

        {isMultiSelect ? (
          <Box paddingX={4} paddingTop={4} flexShrink={0}>
            <Button
              size="sm"
              fullWidth
              className={styles.deleteChartsBtn}
              color="destructive"
              variant="outlined"
              leftIcon={<TrashIcon />}
              onClick={handleDeleteChart}
            >
              Delete {selectedIds?.size ?? 0} charts
            </Button>
          </Box>
        ) : (
          <>
            <Box className={styles.tabsBar} paddingX={4} flexShrink={0}>
              <Tabs
                isFitted
                size="sm"
                colorScheme="green"
                index={chartTabIndex}
                onChange={(index) => setChartTabIndex(index)}
              >
                <TabList>
                  <Tab fontWeight="600">Setup</Tab>
                  <Tab fontWeight="600">Customize</Tab>
                </TabList>
              </Tabs>
            </Box>

            {chartTabIndex === 1 && item ? (
              <Box flex="1" minH={0} overflow="auto">
                <ChartCustomizePanel item={item} isReport={isReport} />
              </Box>
            ) : (
              <Box flex="1" minH={0} overflow="auto">
                {isNewTheme && item && (
                  <Box paddingX={4} paddingTop={4} paddingBottom={2}>
                    {titleField}
                  </Box>
                )}

                {/* TODO: More actionable / useful empty state (only shows up after delete) */}
                {!item && isOpen && (
                  <Text fontSize="sm" fontWeight={500}>
                    Select or create a chart on the grid.
                  </Text>
                )}

                {item && (
                  <Accordion
                    allowToggle
                    index={accordionIndex}
                    onChange={handleAccordionChange}
                    mb={3}
                    paddingX={2}
                  >
                    <AccordionItem border="none">
                      <AccordionButton
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        paddingLeft={2}
                        paddingRight=".625rem"
                        py={2}
                      >
                        <Box
                          flex="1"
                          textAlign="left"
                          fontSize="sm"
                          fontWeight="600"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Text
                            className={labelStyles.sectionLabel}
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Chart Type
                          </Text>
                          {!!chartType && !isSelectableChartType && (
                            <Tooltip
                              label="This chart's settings are fixed and cannot be changed."
                              fontSize="xs"
                            >
                              <span>
                                <InfoOutlineIcon color="gray.400" />
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                        <Flex paddingRight={2.5}>
                          <AccordionIcon ml={2} />
                        </Flex>
                      </AccordionButton>
                      <AccordionPanel marginTop={2} paddingY={0} paddingX={2}>
                        <ChartTypeSelector
                          onSelect={() => setAccordionIndex(-1)}
                          item={item}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}

                <Box paddingX={4}>
                  {panelVisibility.showChartOptionBlock && item && (
                    <>
                      {primaryFilter && (
                        <DimensionSourceSelector
                          item={item}
                          primaryFilter={primaryFilter}
                        />
                      )}

                      {renderMetricSelector()}

                      {isSnowflake &&
                        isReport &&
                        customReportsSnowflakeEnabled &&
                        chartType === 'table' &&
                        id && (
                          <Button
                            size="xs"
                            variant="link"
                            className={styles.useStandardMetricsBtn}
                            onClick={() => {
                              dispatch({
                                type: 'UPDATE_ITEM_DATASOURCE',
                                payload: { id, dataSource: 'mw-gql' },
                              });
                            }}
                          >
                            Use standard table metrics
                          </Button>
                        )}

                      {primaryFilter && selectorConfig?.showSegment && (
                        <SecondaryDimensionSelector
                          item={item}
                          primaryFilter={primaryFilter}
                          forceSegment={selectorConfig?.forceSegment ?? false}
                          showGroupBySegment={
                            (selectorConfig?.showGroupBySegment ?? false) &&
                            !isStackedBarTimeseries
                          }
                          isDisabled={!isSelectableChartType}
                          excludeDimensions={
                            isBarChart ? excludedBreakdownDimensions : undefined
                          }
                        />
                      )}

                      {selectorConfig?.showFilter && (
                        <DataFilterSelector
                          item={item}
                          plotFilterMenu={
                            selectorConfig.plotFilterMenu ?? 'all'
                          }
                        />
                      )}

                      {renderParameterSelector()}
                    </>
                  )}

                  {chartType === 'text' && id && primaryFilter?.id && item && (
                    <Box mb={2}>
                      <TextContentEditor
                        item={item}
                        value={item.plot?.textHtml || ''}
                        onChange={(textHtml) => {
                          dispatch({
                            type: 'UPDATE_ITEM_TEXT_HTML',
                            payload: { id, textHtml },
                          });
                        }}
                        primaryFilterId={primaryFilter.id}
                      />
                      {primaryFilter && isReport && (
                        <Box mt={3}>
                          <ReportParameterSelector
                            item={item}
                            parameters={item.plot?.plotParameters ?? []}
                            primaryFilter={primaryFilter}
                          />
                        </Box>
                      )}
                      <Box mt={3}>
                        <Checkbox
                          isChecked={
                            item.plot?.excludeFromExecutiveSummary ?? false
                          }
                          onChange={(e) => {
                            dispatch({
                              type: 'UPDATE_ITEM_EXCLUDE_FROM_EXECUTIVE_SUMMARY',
                              payload: {
                                id,
                                excludeFromExecutiveSummary: e.target.checked,
                              },
                            });
                          }}
                        >
                          <Text fontSize="sm">
                            Exclude from executive summary
                          </Text>
                        </Checkbox>
                      </Box>
                    </Box>
                  )}

                  {chartType === 'gauge' && id && item && (
                    <Box mb={2}>
                      <Text
                        className={labelStyles.sectionLabel}
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Value
                      </Text>
                      <Input
                        value={item.plot?.gaugeValue ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_VALUE',
                            payload: { id, gaugeValue: e.target.value },
                          });
                        }}
                        placeholder='Enter value (0-1) or handlebar template (e.g., {{FORMAT (SUM "headcount") format="SI"}})'
                        fontSize="sm"
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Primary Text
                      </Text>
                      <Textarea
                        value={item.plot?.gaugePrimaryText || ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_PRIMARY_TEXT',
                            payload: { id, gaugePrimaryText: e.target.value },
                          });
                        }}
                        placeholder='Enter primary text with handlebar syntax (e.g., {{FORMAT (SUM "headcount") format="SI"}})'
                        fontSize="sm"
                        minH="80px"
                        resize="vertical"
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Primary label
                      </Text>
                      <Input
                        value={item.plot?.gaugePrimaryLabel ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_PRIMARY_LABEL',
                            payload: { id, gaugePrimaryLabel: e.target.value },
                          });
                        }}
                        placeholder="Optional label to the right of primary text (e.g. % or handlebar)"
                        fontSize="sm"
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Sub Text
                      </Text>
                      <Textarea
                        value={item.plot?.gaugeSubText || ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_SUB_TEXT',
                            payload: { id, gaugeSubText: e.target.value },
                          });
                        }}
                        placeholder="Enter sub text with handlebar syntax"
                        fontSize="sm"
                        minH="60px"
                        resize="vertical"
                      />
                    </Box>
                  )}

                  {!isNewTheme && item && titleField}

                  {!isNewTheme && item && deleteChartButton}
                </Box>
              </Box>
            )}

            {isNewTheme && item && (
              <Box paddingX={4} flexShrink={0}>
                {deleteChartButton}
              </Box>
            )}
          </>
        )}
      </Box>
    </Portal>
  );
};
